/**
 * WebRTC DataChannel connection manager
 */

import type {
  RTCMessage,
  RTCConnectionState,
  RTCEventHandlers,
  SignalingData,
} from './types.ts';

/**
 * WebRTC connection configuration
 */
const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

/**
 * Manages WebRTC peer-to-peer connection with DataChannel
 */
export class RTCConnectionManager {
  private peerConnection: RTCPeerConnection;
  private dataChannel: RTCDataChannel | null = null;
  private handlers: RTCEventHandlers;
  private iceCandidates: RTCIceCandidateInit[] = [];

  constructor(handlers: RTCEventHandlers) {
    this.handlers = handlers;
    this.peerConnection = new RTCPeerConnection(RTC_CONFIG);
    this.setupPeerConnection();
  }

  /**
   * Setup peer connection event listeners
   */
  private setupPeerConnection(): void {
    this.peerConnection.onicecandidate = event => {
      if (event.candidate) {
        this.iceCandidates.push(event.candidate.toJSON());
      }
    };

    this.peerConnection.onconnectionstatechange = () => {
      this.handlers.onConnectionStateChange(
        this.peerConnection.connectionState as RTCConnectionState
      );
    };

    this.peerConnection.ondatachannel = event => {
      this.setupDataChannel(event.channel);
    };

    this.peerConnection.onicegatheringstatechange = () => {
      if (this.peerConnection.iceGatheringState === 'complete') {
        // ICE gathering is complete
      }
    };
  }

  /**
   * Setup data channel event listeners
   */
  private setupDataChannel(channel: RTCDataChannel): void {
    this.dataChannel = channel;

    channel.onopen = () => {
      console.log('DataChannel opened');
    };

    channel.onclose = () => {
      console.log('DataChannel closed');
    };

    channel.onmessage = event => {
      try {
        const message: RTCMessage = JSON.parse(event.data);
        this.handlers.onMessage(message);
      } catch {
        this.handlers.onError(new Error('Failed to parse message'));
      }
    };

    channel.onerror = () => {
      this.handlers.onError(new Error('DataChannel error'));
    };
  }

  /**
   * Create an offer to start connection as the initiator
   */
  async createOffer(): Promise<SignalingData> {
    try {
      // Create data channel
      this.dataChannel = this.peerConnection.createDataChannel('game', {
        ordered: true,
      });
      this.setupDataChannel(this.dataChannel);

      // Create offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Wait for ICE gathering to complete
      await this.waitForIceGathering();

      return {
        offer: offer,
        iceCandidates: this.iceCandidates,
      };
    } catch (_error) {
      this.handlers.onError(new Error('Failed to create offer'));
      throw _error;
    }
  }

  /**
   * Accept an offer and create an answer
   */
  async acceptOffer(signalingData: SignalingData): Promise<SignalingData> {
    try {
      if (!signalingData.offer) {
        throw new Error('No offer provided');
      }

      // Set remote description
      await this.peerConnection.setRemoteDescription(signalingData.offer);

      // Add ICE candidates
      if (signalingData.iceCandidates) {
        for (const candidate of signalingData.iceCandidates) {
          await this.peerConnection.addIceCandidate(candidate);
        }
      }

      // Create answer
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      // Wait for ICE gathering to complete
      await this.waitForIceGathering();

      return {
        answer: answer,
        iceCandidates: this.iceCandidates,
      };
    } catch (_error) {
      this.handlers.onError(new Error('Failed to accept offer'));
      throw _error;
    }
  }

  /**
   * Complete connection with the answer
   */
  async completeConnection(signalingData: SignalingData): Promise<void> {
    try {
      if (!signalingData.answer) {
        throw new Error('No answer provided');
      }

      // Set remote description
      await this.peerConnection.setRemoteDescription(signalingData.answer);

      // Add ICE candidates
      if (signalingData.iceCandidates) {
        for (const candidate of signalingData.iceCandidates) {
          await this.peerConnection.addIceCandidate(candidate);
        }
      }
    } catch (_error) {
      this.handlers.onError(new Error('Failed to complete connection'));
      throw _error;
    }
  }

  /**
   * Send a message through the data channel
   */
  sendMessage(message: RTCMessage): void {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      this.handlers.onError(new Error('DataChannel not ready'));
      return;
    }

    try {
      this.dataChannel.send(JSON.stringify(message));
    } catch {
      this.handlers.onError(new Error('Failed to send message'));
    }
  }

  /**
   * Wait for ICE gathering to complete with timeout
   */
  private waitForIceGathering(): Promise<void> {
    return new Promise(resolve => {
      if (this.peerConnection.iceGatheringState === 'complete') {
        resolve();
      } else {
        const checkState = () => {
          if (this.peerConnection.iceGatheringState === 'complete') {
            clearTimeout(timeoutId);
            this.peerConnection.removeEventListener(
              'icegatheringstatechange',
              checkState
            );
            resolve();
          }
        };

        // Set up timeout to resolve after 5 seconds if ICE gathering doesn't complete
        const timeoutId = window.setTimeout(() => {
          this.peerConnection.removeEventListener(
            'icegatheringstatechange',
            checkState
          );
          // Resolve even if ICE gathering isn't complete
          // This allows the connection to proceed with the candidates we have
          resolve();
        }, 5000);

        this.peerConnection.addEventListener(
          'icegatheringstatechange',
          checkState
        );
      }
    });
  }

  /**
   * Get current connection state
   */
  getConnectionState(): RTCConnectionState {
    return this.peerConnection.connectionState as RTCConnectionState;
  }

  /**
   * Close the connection
   */
  close(): void {
    if (this.dataChannel) {
      this.dataChannel.close();
    }
    this.peerConnection.close();
  }
}
